import {
  fetchEmpresaLogada,
  fetchEmpresasUser,
} from "@/app/api/configuracoes/empresas";
import { updateEmpresaAtiva } from "@/app/api/configuracoes/empresas";
import { useCurrentUserWithStatus } from "@/hooks/use-current-user";
import feedback from "@/utils/feedback";
import { signOut, useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EmpresaLogada } from "../types/empresas/empresas";

interface EmpresaContextProps {
  empresa: EmpresaLogada | null;
  empresas: Array<{ id: string; nomeFantasia: string; logo: string }>;
  loading: boolean;
  needsSelection: boolean; // ✨ NOVO: Indica se precisa selecionar empresa
  imageVersion: number; // ✨ NOVO: Versão das imagens para cache busting
  setEmpresa: (empresa: EmpresaLogada) => void;
  reloadEmpresa: () => Promise<void>;
  loadEmpresas: () => Promise<void>;
  refreshEmpresas: () => Promise<void>; // ✨ NOVO: Força atualização da lista de empresas
  changeEmpresa: (empresaId: string) => Promise<void>;
  invalidateImageCache: () => void; // ✨ NOVO: Invalida cache das imagens
}

const EmpresaContext = createContext<EmpresaContextProps | undefined>(
  undefined,
);

export const EmpresaProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    status,
    isLoading: sessionLoading,
  } = useCurrentUserWithStatus();
  const { update } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  const [empresa, setEmpresa] = useState<EmpresaLogada | null>(null);
  const [empresas, setEmpresas] = useState<
    Array<{ id: string; nomeFantasia: string; logo: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [needsSelection, setNeedsSelection] = useState(false); // ✨ NOVO
  const [isInitialized, setIsInitialized] = useState(false);
  const [imageVersion, setImageVersion] = useState(Date.now()); // ✨ NOVO: Timestamp para cache busting
  const retryCountRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const hasRedirectedRef = useRef(false); // ✨ NOVO: Evita loops de redirect
  const pendingCompanySwitchRef = useRef<string | null>(null);

  const reloadEmpresa = useCallback(async () => {
    if (!user?.id || !user?.contratanteId) {
      return;
    }

    try {
      // Verifica se user tem empresaAtual ou empresaPadrao
      const empresaId = user.empresaAtual || user.empresaPadrao;

      if (!empresaId) {
        console.log("[EmpresaContext] Usuário sem empresa selecionada");
        // ✨ Master pode trabalhar sem empresa (opcional)
        if (user?.master) {
          setEmpresa(null);
          setNeedsSelection(false);
          setIsInitialized(true);
        } else {
          setNeedsSelection(true);
          setIsInitialized(true);
        }
        return;
      }

      const fetchedEmpresa = await fetchEmpresaLogada(
        user.id,
        user.contratanteId,
      );

      if (fetchedEmpresa) {
        setEmpresa(fetchedEmpresa);
        setNeedsSelection(false);
        setIsInitialized(true);
        retryCountRef.current = 0;
      } else {
        console.log("[EmpresaContext] Empresa não encontrada");
        // ✨ Master pode trabalhar sem empresa
        if (user?.master) {
          setEmpresa(null);
          setNeedsSelection(false);
        } else {
          setNeedsSelection(true);
        }
        setIsInitialized(true);
      }
    } catch (error) {
      console.error("[EmpresaContext] Erro ao carregar empresa:", error);
      // ✨ Master pode continuar mesmo com erro
      if (user?.master) {
        setEmpresa(null);
        setNeedsSelection(false);
      } else {
        setNeedsSelection(true);
      }
      setIsInitialized(true);
    }
  }, [
    user?.id,
    user?.contratanteId,
    user?.master,
    user?.empresaAtual,
    user?.empresaPadrao,
  ]);

  const loadEmpresas = useCallback(async () => {
    if (empresas.length > 0) return;
    if (!user?.contratanteId) return;

    try {
      const fetchedEmpresas = await fetchEmpresasUser();
      if (fetchedEmpresas) {
        setEmpresas(fetchedEmpresas);
      }
    } catch (error) {
      console.error(
        "[EmpresaContext] Erro ao carregar lista de empresas:",
        error,
      );
    }
  }, [empresas.length, user?.contratanteId, user?.master]);

  // ✨ NOVO: Força atualização da lista de empresas (usado após criar/deletar empresas)
  const refreshEmpresas = useCallback(async () => {
    if (!user?.contratanteId) return;

    try {
      const fetchedEmpresas = await fetchEmpresasUser();
      if (fetchedEmpresas) {
        setEmpresas(fetchedEmpresas);
        console.log(
          "[EmpresaContext] Lista de empresas atualizada:",
          fetchedEmpresas.length,
        );
      }
    } catch (error) {
      console.error(
        "[EmpresaContext] Erro ao atualizar lista de empresas:",
        error,
      );
    }
  }, [user?.contratanteId]);

  const invalidateImageCache = useCallback(() => {
    setImageVersion(Date.now());
  }, []);

  const changeEmpresa = useCallback(
    async (empresaId: string) => {
      if (!empresaId || loading) return;

      setLoading(true);
      try {
        // 1. ✅ Redireciona para o dashboard PRIMEIRO para evitar dados inconsistentes
        if (pathname !== "/dashboard") {
          router.push("/dashboard");
        }

        // 2. Update otimista - atualiza UI imediatamente
        const novaEmpresa = empresas.find((e) => e.id === empresaId);
        if (novaEmpresa) {
          setEmpresa({
            id: novaEmpresa.id,
            nomeFantasia: novaEmpresa.nomeFantasia,
            logo: novaEmpresa.logo,
            email: empresa?.email || null,
            contratanteId: user?.contratanteId || "",
            configId: empresa?.configId || null,
          });
        }

        // 3. ✅ Modo suporte: apenas atualiza sessão, NÃO altera o banco
        if (user?.isSuporte) {
          await update({ suporteEmpresaId: empresaId });
          await reloadEmpresa();
        } else {
          // Fluxo normal: atualiza backend em paralelo
          const [updateResult] = await Promise.all([
            updateEmpresaAtiva(empresaId),
            update({
              user: {
                empresaAtual: empresaId,
                empresaPadrao: empresaId,
              },
            }),
          ]);

          if (!updateResult?.data) {
            throw new Error("Erro ao atualizar empresa no backend");
          }

          // 4. Recarrega dados completos da empresa em background
          await reloadEmpresa();
        }
      } catch (error) {
        console.error("Erro ao trocar empresa:", error);
        // Reverte para empresa anterior em caso de erro
        await reloadEmpresa();
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      empresas,
      loading,
      user?.contratanteId,
      user?.isSuporte,
      empresa?.configId,
      update,
      reloadEmpresa,
      router,
      pathname,
    ],
  );

  useEffect(() => {
    try {
      // Aguarda a sessão estar autenticada antes de tentar carregar empresa
      if (status === "loading") {
        return;
      }

      if (status === "unauthenticated") {
        setIsInitialized(true);
        return;
      }

      // Evita carregar múltiplas vezes
      if (hasLoadedRef.current) {
        return;
      }

      // Sessão autenticada, carrega empresa
      if (status === "authenticated") {
        if (user?.id && user?.contratanteId) {
          hasLoadedRef.current = true;
          reloadEmpresa();
        } else if (retryCountRef.current < 3) {
          // User autenticado mas dados ainda não disponíveis, retry
          const timer = setTimeout(() => {
            retryCountRef.current += 1;
            setIsInitialized((prev) => prev);
          }, 500);
          return () => clearTimeout(timer);
        } else {
          setIsInitialized(true);
        }
      }
    } catch (error) {
      console.error("[EmpresaContext] Erro no useEffect:", error);
    }
  }, [
    status,
    user?.id,
    user?.contratanteId,
    user?.master,
    reloadEmpresa,
    isInitialized,
  ]);

  useEffect(() => {
    // Só carrega lista de empresas após autenticação completa
    if (status === "authenticated" && user?.contratanteId) {
      loadEmpresas();
    }
  }, [status, user?.contratanteId, loadEmpresas]);

  useEffect(() => {
    if (status !== "authenticated" || !isInitialized) {
      return;
    }

    const pendingEmpresaId = sessionStorage.getItem("pendingCompanySwitch");
    if (
      !pendingEmpresaId ||
      pendingCompanySwitchRef.current === pendingEmpresaId
    ) {
      return;
    }

    pendingCompanySwitchRef.current = pendingEmpresaId;
    sessionStorage.removeItem("pendingCompanySwitch");

    if (pendingEmpresaId === empresa?.id) {
      return;
    }

    changeEmpresa(pendingEmpresaId).catch((error) => {
      console.error("Erro ao aplicar troca de empresa pendente:", error);
      pendingCompanySwitchRef.current = null;
    });
  }, [status, isInitialized, empresa?.id, changeEmpresa]);

  // ✅ Quando o modo suporte muda (entrar/sair), força reload completo
  const prevContratanteIdRef = useRef(user?.contratanteId);
  useEffect(() => {
    if (
      status === "authenticated" &&
      user?.contratanteId &&
      prevContratanteIdRef.current &&
      prevContratanteIdRef.current !== user.contratanteId
    ) {
      console.log(
        "[EmpresaContext] Contratante mudou (suporte) - recarregando dados",
      );
      // Limpa estado e força reload
      setEmpresas([]);
      setEmpresa(null);
      hasLoadedRef.current = false;
      setIsInitialized(false);
      setNeedsSelection(false);
      hasRedirectedRef.current = false;
      reloadEmpresa();
    }
    prevContratanteIdRef.current = user?.contratanteId;
  }, [user?.contratanteId, status, reloadEmpresa]);

  // ✨ Se usuário não tem empresa selecionada, define a primeira disponível
  useEffect(() => {
    if (!isInitialized || hasRedirectedRef.current) return;

    // Não redireciona usuários master
    if (user?.master) return;

    // Se precisa selecionar empresa e tem empresas disponíveis
    if (needsSelection && empresas.length > 0 && status === "authenticated") {
      console.log(
        "[EmpresaContext] Auto-selecionando primeira empresa disponível",
      );
      hasRedirectedRef.current = true;

      // Seleciona automaticamente a primeira empresa
      const primeiraEmpresa = empresas[0];
      changeEmpresa(primeiraEmpresa.id);
    }

    // 🆕 Se usuário não tem empresas e não é master, desloga
    if (
      needsSelection &&
      empresas.length === 0 &&
      status === "authenticated" &&
      isInitialized &&
      !user?.master
    ) {
      console.warn(
        "[EmpresaContext] Usuário sem acesso a nenhuma empresa - fazendo logout",
      );
      setLoading(false);
      setNeedsSelection(false);

      // Armazena mensagem de erro
      feedback.error(
        "Você não tem acesso a nenhuma empresa. Entre em contato com o administrador do sistema.",
      );

      // Aguarda um momento e faz logout
      setTimeout(() => {
        signOut({ callbackUrl: "/auth/login" });
      }, 100);
    }
  }, [
    needsSelection,
    empresas,
    status,
    user?.master,
    isInitialized,
    changeEmpresa,
  ]);

  const value = useMemo(
    () => ({
      empresa,
      empresas,
      loading,
      needsSelection, // ✨ NOVO
      imageVersion, // ✨ NOVO
      setEmpresa,
      reloadEmpresa,
      loadEmpresas,
      refreshEmpresas, // ✨ NOVO
      changeEmpresa,
      invalidateImageCache, // ✨ NOVO
    }),
    [
      empresa,
      empresas,
      loading,
      needsSelection,
      imageVersion,
      reloadEmpresa,
      loadEmpresas,
      refreshEmpresas,
      changeEmpresa,
      invalidateImageCache,
    ],
  );

  // Mostra loading inicial enquanto sessão está carregando
  if (
    sessionLoading ||
    (status === "authenticated" && !isInitialized && !user?.id)
  ) {
    return (
      <EmpresaContext.Provider value={value}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <p style={{ color: "#666", fontSize: "14px" }}>
            {sessionLoading
              ? "Autenticando..."
              : "Carregando dados do usuário..."}
          </p>
        </div>
      </EmpresaContext.Provider>
    );
  }

  return (
    <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
  );
};

export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (context === undefined) {
    throw new Error("useEmpresa deve ser usado dentro de um EmpresaProvider");
  }
  return context;
};
